import type { FunctionComponent } from "react";
import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import { ScrollArea } from "@similarweb/ui-components";

import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import QualificationSideBarContext from "app/components/QualificationSideBar/QualificationSidebarContext";
import type { ISideBarGenericProps } from "app/pages/website-analysis/components/qualification-sidebar/components/helpers";
import { MemoizedSiteInfo } from "app/pages/website-analysis/components/qualification-sidebar/components/SiteInfo";
import { ContentWrapper } from "app/pages/website-analysis/components/qualification-sidebar/components/StyledComponents";
import { isHasNoWWPermissions } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { MemoizedHeader } from "./components/header/Header";
import { MemoizedEngagementMetrics } from "./metrics/EngagementMetrics";
import { MemoizedTopCountries } from "./metrics/TopCountries";
import { MemoizedTrafficSources } from "./metrics/TrafficSources";
import { allTrackers } from "app/services/track/track";

const SideBarContainer = styled(FlexColumn)`
    height: 100%;
    width: 100%;
`;

const ContentCardsContainer = styled(FlexColumn)`
    overflow: hidden;
    box-sizing: border-box;
    height: 100%;
`;

export interface ISideBarComponentProps extends ISideBarGenericProps {
    isWWOApiController: boolean;
}

export const QualificationSideBarContainer: FunctionComponent = () => {
    const { qualificationSideBarSelection, closeQualificationSideBar } = React.useContext(QualificationSideBarContext);
    const duration = "3m";
    const country = "999";
    const emptyStateRef = useRef({});
    const scrollWrapperRef = useRef(null);
    const [isScroll, setIsScroll] = useState(false);
    const [isGlobalEmptyState, setGlobalEmptyState] = useState(false);
    const { domain, favicon, componentId } = qualificationSideBarSelection?.data ?? {};

    const isWWOApiController = isHasNoWWPermissions(country);
    const wwoDuration = isWWOApiController ? "3m" : duration;
    // const isWorldWideAvailable = salesSettingsHelper.getAllowedCountries().find((country) => country.id === 999);
    ////////////  events ////////////
    const onEmptyData = React.useCallback((id) => {
        emptyStateRef.current = {
            ...emptyStateRef.current,
            [id]: true,
        };
        if (Object.keys(emptyStateRef.current).length === 4) {
            allTrackers.dispatchTrackEvent("Download", "show", `QualificationSidebar/no-data/${domain || ""}`);
            setGlobalEmptyState(true);
        }
    }, []);
    const onScroll = React.useCallback(({ topPosition }) => {
        topPosition > 0 ? setIsScroll(true) : setIsScroll(false);
    }, []);
    ////////////////////////////////////////////////

    useEffect(() => {
        // reset the scrolling
        if (scrollWrapperRef.current && scrollWrapperRef.current.state.topPosition > 0) {
            scrollWrapperRef.current.scrollTop();
        }
    }, [qualificationSideBarSelection]);

    useEffect(() => {
        if (isGlobalEmptyState) {
            emptyStateRef.current = {};
            setGlobalEmptyState(false);
        }
    }, [qualificationSideBarSelection]);

    return (
        <SideBarContainer>
            <MemoizedHeader
                duration={duration}
                country={country}
                qualificationSideBarSelection={qualificationSideBarSelection}
                closeQualificationSideBar={closeQualificationSideBar}
                isScrolling={isScroll}
            />
            <ContentCardsContainer>
                {isGlobalEmptyState ? (
                    <ContentWrapper
                        height="100%"
                        isNoData={true}
                    >
                        <NoDataLandscape
                            title="qualification.sidebar.complete.no-data.title"
                            subtitle="qualification.sidebar.complete.no-data.subtitle"
                        />
                    </ContentWrapper>
                ) : (
                    <ScrollArea
                        verticalScrollbarStyle={{
                            borderRadius: 5,
                        }}
                        horizontal={false}
                        smoothScrolling={true}
                        minScrollSize={48}
                        ref={scrollWrapperRef}
                        onScroll={onScroll}
                    >
                        <ContentWrapper
                            height="100%"
                            isNoData={false}
                        >
                            <MemoizedSiteInfo country={country} />
                            <MemoizedEngagementMetrics
                                webSource={devicesTypes.TOTAL}
                                duration={wwoDuration}
                                country={country}
                                onEmptyData={onEmptyData}
                                isWWOApiController={isWWOApiController}
                                componentId={componentId}
                            />
                            <MemoizedTrafficSources
                                webSource={devicesTypes.DESKTOP}
                                duration={wwoDuration}
                                country={country}
                                onEmptyData={onEmptyData}
                                isWWOApiController={isWWOApiController}
                                componentId={componentId}
                            />
                            <MemoizedTopCountries
                                webSource={devicesTypes.DESKTOP}
                                duration={wwoDuration}
                                country={country}
                                onEmptyData={onEmptyData}
                                componentId={componentId}
                            />
                            {/*DEPRECATED : TODO: consider replacing with a new kw 3.0 data*/}
                            {/*{isWorldWideAvailable && (*/}
                            {/*    <MemoizedTopNonBrandedKeywords*/}
                            {/*        webSource={devicesTypes.DESKTOP}*/}
                            {/*        duration={duration}*/}
                            {/*        country={country}*/}
                            {/*        onEmptyData={onEmptyData}*/}
                            {/*    />*/}
                            {/*)}*/}
                        </ContentWrapper>
                    </ScrollArea>
                )}
            </ContentCardsContainer>
        </SideBarContainer>
    );
};
